
import { useState, useEffect, useCallback, useRef } from "react";
import { User, AuthError } from "@supabase/supabase-js";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { AuthStateManager } from "@/components/providers/auth/state/AuthStateManager";
import { useQueryClient } from "@tanstack/react-query";
import { logger } from "@/utils/logger";

// Lista över localStorage-nycklar som ska behållas vid utloggning
const PRESERVED_STORAGE_KEYS = ['tts-voice', 'voice-speed', 'billboard_mode'];

const clearAuthStorage = () => {
  // Only remove auth-related keys, preserve everything else including PKCE data
  const authKeys = [
    'supabase.auth.token',
    'sb-ftchydorvlpfemysaoqn-auth-token',
    'sb-ftchydorvlpfemysaoqn-auth-token-code-verifier'
  ];
  
  authKeys.forEach(key => {
    localStorage.removeItem(key);
  });
};

export const useAuthSession = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const mounted = useRef(true);
  const authManager = useRef(AuthStateManager.getInstance());
  const authListenerRef = useRef<{ unsubscribe: () => void } | null>(null);
  const lastMoveAttempt = useRef<number>(0);
  const initAttempts = useRef<number>(0);

  const handleSessionError = useCallback((error?: AuthError, shouldNavigate: boolean = true) => {
    if (!mounted.current) return;

    // On auth callback route, let the callback page handle everything
    if (location.pathname.includes('/auth/callback')) {
      logger.debug('[useAuthSession] On /auth/callback, deferring session error handling');
      return;
    }

    // For temporary session issues, don't immediately clear user state
    const isTemporaryError = error?.message?.includes('Session not found') || 
                           error?.message?.includes('Invalid session');
    
    if (!isTemporaryError) {
      setUser(null);
    }

    // Only navigate and show errors for non-temporary issues
    if (shouldNavigate && !isTemporaryError) {
      // Only navigate to login if not already there
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }

      if (error) {
        console.error('[useAuthSession] Session error:', error);
        let errorMessage = "Det gick inte att kontrollera inloggningsstatus. Försök igen.";
        if (error.message?.includes('auth code and code verifier')) {
          errorMessage = "Inloggningsfel - försök logga in igen.";
        }
        toast({
          title: "Session Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } else if (isTemporaryError) {
      logger.debug('[useAuthSession] Temporary session error, will retry:', error?.message);
    }
  }, [navigate, location.pathname]);

  // Move past activities to history
  const movePastActivities = async (sessionToken: string) => {
    // Begränsa hur ofta vi försöker köra denna funktion
    const now = Date.now();
    if (now - lastMoveAttempt.current < 60000) { // Minst 1 minut mellan försök
      console.log('Skipping move past activities on login, too soon since last attempt');
      return;
    }
    
    lastMoveAttempt.current = now;
    
    try {
      console.log('Attempting to move past activities after sign in');
      const SUPABASE_URL = "https://ftchydorvlpfemysaoqn.supabase.co";
      const response = await fetch(`${SUPABASE_URL}/functions/v1/move-past-activities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
      });

      if (!response.ok) {
        console.error('Failed to move past activities after login:', response.status);
        return;
      }

      const result = await response.json();
      console.log('Past activities processed after login:', result);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['activities-with-registrations'] });
      queryClient.invalidateQueries({ queryKey: ['historical-activities-with-registrations'] });
      queryClient.invalidateQueries({ queryKey: ['billboard-events'] });
      queryClient.invalidateQueries({ queryKey: ['user-accessible-activities'] });
    } catch (error) {
      console.error('Error moving past activities after login:', error);
    }
  };

  // Main auth effect
  useEffect(() => {
    logger.debug('Setting up auth listener and session check');

    const setupAuth = async () => {
      if (!mounted.current) return;
      
      // Do not interfere on the OAuth callback route; AuthCallback manages the flow
      if (location.pathname.includes('/auth/callback')) {
        logger.debug('[useAuthSession] On /auth/callback, skipping initial session handling');
        setIsLoading(false);
        return;
      }
      
      try {
        initAttempts.current += 1;
        logger.debug(`[useAuthSession] Initializing session check, attempt ${initAttempts.current}`);
        
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) throw error;

        if (!mounted.current) return;

        if (session?.user) {
          logger.debug('[useAuthSession] Found existing session for', session.user.email);
          setUser(session.user);
          queryClient.invalidateQueries({ queryKey: ['profile'] });
          
          // Initiate movement of past activities when user logs in
          if (session.access_token) {
            movePastActivities(session.access_token).catch(console.error);
          }
          
          if (location.pathname === '/login') {
            navigate('/', { replace: true });
          }
        } else {
          logger.debug('[useAuthSession] No session found');
          // For initial load without session, navigate to login but don't show error
          if (location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
        }
      } catch (error) {
        console.error('[useAuthSession] Initial session check error:', error);
        // Use retry logic for initial session errors
        handleSessionError(error as AuthError, false);
      } finally {
        if (mounted.current) {
          setIsLoading(false);
        }
      }
    };

    // Set up auth state change listener if it doesn't exist
    if (!authListenerRef.current) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        logger.debug('[useAuthSession] Auth state changed:', event);
        
        if (!mounted.current) return;

        switch (event) {
          case 'SIGNED_IN':
            if (session?.user) {
              setUser(session.user);
              queryClient.invalidateQueries({ queryKey: ['profile'] });
              queryClient.invalidateQueries({ queryKey: ['events'] });
              queryClient.invalidateQueries({ queryKey: ['billboard-events'] });
              queryClient.invalidateQueries({ queryKey: ['user-accessible-activities'] });
              
              // Defer async operations to avoid deadlock
              setTimeout(() => {
                if (session.access_token) {
                  movePastActivities(session.access_token).catch(console.error);
                }
              }, 0);
              
              if (location.pathname === '/login') {
                navigate('/', { replace: true });
              }
            }
            break;

          case 'SIGNED_OUT':
            setUser(null);
            queryClient.clear();
            if (location.pathname.includes('/auth/callback')) {
              logger.debug('[useAuthSession] SIGNED_OUT during /auth/callback, skipping storage clear and navigation');
              break;
            }
            clearAuthStorage();
            if (location.pathname !== '/login') {
              navigate('/login', { replace: true });
            }
            break;

          case 'TOKEN_REFRESHED':
            logger.debug('[useAuthSession] Token refreshed');
            if (session?.user) {
              setUser(session.user);
            }
            break;

          case 'USER_UPDATED':
            if (session?.user) {
              setUser(session.user);
              queryClient.invalidateQueries({ queryKey: ['profile'] });
            }
            break;
        }
      });

      authListenerRef.current = subscription;
    }

    mounted.current = true;
    setupAuth();
    
    return () => {
      mounted.current = false;
      if (authListenerRef.current) {
        authListenerRef.current.unsubscribe();
        authListenerRef.current = null;
      }
    };
  }, [navigate, location.pathname, handleSessionError, queryClient]);

  // Removed redundant visibility change handler - already handled by supabase client

  return { user, isLoading };
};
